<template>
    <div
        id="lt-yantr-v2"
        class="advanced-calc debt-to-income">
        <div class="container-width">
            <div class="advanced-calc-filter">
                <div class="filter-inputs">
                    <div>
                        <number-input
                            id="annualIncome"
                            v-model="AnnualIncome"
                            :label="labelAnnualIncome"
                            :tooltip-link="tooltipAnnualIncome"
                            inputmode="decimal"
                        />
                    </div>
                    <div>
                        <number-input
                            id="totalMonthlyDebtPayments"
                            v-model="TotalMonthlyDebtPayments"
                            :label="labelTotalMonthlyDebtPayments"
                            :tooltip-link="tooltipTotalMonthlyDebtPayments"
                            :readonly="ShowAdvancedOptions"
                            inputmode="decimal"
                            @input="updatePaymentValues()"
                        />
                    </div>
                </div>
                <!-- Advanced Options start -->
                <transition name="slide">
                    <div
                        v-show="ShowAdvancedOptions"
                        class="advanced-options"
                        :class="{ 'all-options': ShowAdvancedOptions, 'less-options' : !ShowAdvancedOptions }">
                        <div class="advanced-header">
                            Itemized Monthly Debt
                        </div>
                        <div class="filter-inputs">
                            <div>
                                <number-input
                                    id="rentPayment"
                                    v-model="RentPayment"
                                    :label="labelRentPayment"
                                    inputmode="decimal"
                                    @input="updateTotalMonthlyDebtPayments()"
                                />
                            </div>
                            <div>
                                <number-input
                                    id="mortgagePayment"
                                    v-model="MortgagePayment"
                                    :label="labelMortgagePayment"
                                    inputmode="decimal"
                                    @input="updateTotalMonthlyDebtPayments()"
                                />
                            </div>
                            <div>
                                <number-input
                                    id="homeEquityLoanPayment"
                                    v-model="HomeEquityLoanPayment"
                                    :label="labelHomeEquityLoanPayment"
                                    inputmode="decimal"
                                    @input="updateTotalMonthlyDebtPayments()"
                                />
                            </div>
                            <div>
                                <number-input
                                    id="creditCardPayment"
                                    v-model="CreditCardPayment"
                                    :label="labelCreditCardPayment"
                                    inputmode="decimal"
                                    @input="updateTotalMonthlyDebtPayments()"
                                />
                            </div>
                            <div>
                                <number-input
                                    id="autoLoanOrLeasePayment"
                                    v-model="AutoLoanOrLeasePayment"
                                    :label="labelAutoLoanOrLeasePayment"
                                    inputmode="decimal"
                                    @input="updateTotalMonthlyDebtPayments()"
                                />
                            </div>
                            <div>
                                <number-input
                                    id="studentLoanPayment"
                                    v-model="StudentLoanPayment"
                                    :label="labelStudentLoanPayment"
                                    inputmode="decimal"
                                    @input="updateTotalMonthlyDebtPayments()"
                                />
                            </div>
                            <div>
                                <number-input
                                    id="personalLoanPayment"
                                    v-model="PersonalLoanPayment"
                                    :label="labelPersonalLoanPayment"
                                    inputmode="decimal"
                                    @input="updateTotalMonthlyDebtPayments()"
                                />
                            </div>
                            <div>
                                <number-input
                                    id="otherDebtPayments"
                                    v-model="OtherDebtPayments"
                                    :label="labelOtherDebtPayments"
                                    inputmode="decimal"
                                    @input="updateTotalMonthlyDebtPayments()"
                                />
                            </div>
                        </div>
                    </div>
                </transition>
                <!-- Advanced Options end-->
                <div class="controls">
                    <button
                        class="show-hide-options"
                        @click="viewMoreClick()">
                        <span
                            class="lt-icons"
                            :class="{ 'lt4-MinusCircle': ShowAdvancedOptions, 'lt4-PlusCircle' : !ShowAdvancedOptions}"></span> Itemized Monthly Debt
                    </button>
                    <div class="reset-sec">
                        <button
                            class="reset-calc"
                            @click="resetFilter()">
                            <span class="lt-icons lt4-Refresh"></span> Reset Calculator
                        </button>
                        <advertising-disclosures
                            :disclosure-id="DisclosureId1"
                            :disclosure-text="DisclosureText"
                            :disclosure-tokens="DisclosureTokens"
                        />
                    </div>
                </div>
            </div>
            <div class="offer-btn-sec">
                <h5>Your debt to income ratio</h5>
                <button
                    class="main-btn btn-green"
                    type="submit"
                    @click="redirect('_blank')">
                    <div class="dti-ratio text-center">
                        <div class="ratio-value">
                            {{ debtToIncomeRatio }} %
                        </div>
                        <div class="free-offers">
                            {{ DestinationButtonText }}
                        </div>
                    </div>
                </button>
                <advertising-disclosures
                    :disclosure-id="DisclosureId2"
                    :disclosure-text="DisclosureText"
                    :disclosure-tokens="DisclosureTokens"
                />
            </div>
            <div class="calculation-section">
                <div class="left-section">
                    <h3>Your Debt-to-Income Ratio</h3>
                    <p
                        class="debt-per"
                        :class="setColor(debtToIncomeRatio)">
                        {{ debtToIncomeRatio }} %
                    </p>
                    <p>Your DTI ratio helps lenders understand how well you’d manage your new mortgage payment, along with your other monthly obligations. Keep your ratio at or below 43% to boost your chances of mortgage approval.</p>
                    <!-- Range Slider start-->
                    <div class="slider-parent">
                        <vue-slider
                            v-model="DebtToIncomeRatioValue"
                            :height="slider.height"
                            :process-style="slider.processStyle"
                            :tooltip="slider.tooltipAlways"
                            :bg-style="slider.bgStyle"
                            :marks="slider.marks"
                            :clickable="slider.clickable"
                            :disabled="slider.disabled"
                        />
                        <div class="labels">
                            <div class="good">
                                good
                            </div>
                            <div class="fair">
                                fair
                            </div>
                            <div class="high">
                                high
                            </div>
                        </div>
                        <div class="range-label">
                            <div class="text-one">
                                <label>0%</label>
                            </div>
                            <div class="text-two">
                                <label>21%</label>
                            </div>
                            <div class="text-three">
                                <label>36%</label>
                            </div>
                            <div class="text-four">
                                <label>100%</label>
                            </div>
                        </div>
                    </div>
                    <!-- Range Slider end-->
                    <div class="calc-box">
                        <div class="calc-box-section">
                            <label>Monthly debt</label>
                            <h3 class="value">
                                ${{ currencyFilter(TotalMonthlyDebtPayments) }}
                            </h3>
                        </div>
                        <div class="sign">
                            ÷
                        </div>
                        <div class="calc-box-section">
                            <label>Gross monthly income</label>
                            <h3 class="value">
                                ${{ currencyFilter(GrossMonthlyIncome) }}
                            </h3>
                        </div>
                        <div class="sign">
                            =
                        </div>
                        <div class="calc-box-section">
                            <label>Debt to income</label>
                            <h3
                                class="value"
                                :class="setColor(debtToIncomeRatio)">
                                {{ debtToIncomeRatio }}%
                            </h3>
                        </div>
                    </div>
                    <lt-logo v-if="ltLogo==1">
                        <small>NMLS#1136 Terms & Conditions Apply</small>
                    </lt-logo>
                </div>
                <div class="right-section text-center">
                    <h3>Debt-to-Income Breakdown</h3>
                    <div class="circle--wrap">
                        <vue-chart
                            :data="pieChartData"
                            :options="pieChartOptions"
                            :width="150"
                            :height="150"
                            type="pie"
                        />
                    </div>
                    <div class="summary">
                        <div class="left">
                            <label><span class="green-bg"></span>Monthly Debt</label>
                            <span class="value">${{ currencyFilter(TotalMonthlyDebtPayments) }}</span>
                        </div>
                        <div class="right">
                            <label><span class="deep-verde-bg"></span>Remaining monthly income</label>
                            <span class="value">${{ currencyFilter(NetIncome) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import VueChart from 'vue-chart-js';
    import vueSlider from 'vue-slider-component';
    import Accounting from 'accounting';
    import VueScrollTo from 'vue-scrollto';
    import DefaultTooltip from '~/constants/v2/tooltip-defaults';
    import BaseMixin from '~/mixin/v2/base-mixin';
    import LtLogo from '~/components/lt-logo.vue';

    export default {
        components: {
            vueSlider,
            LtLogo,
            VueChart,
        },
        directives: {
            VueScrollTo,
        },
        mixins: [BaseMixin],
        props: {
            annualIncome: {
                default: '80000',
            },
            labelAnnualIncome: {
                default: 'Gross Annual Income',
            },
            tooltipAnnualIncome: {
                default: 'annual-income',
            },
            labelTotalMonthlyDebtPayments: {
                default: 'Total Monthly Debt Payments',
            },
            totalMonthlyDebtPayments: {
                default: '2750',
            },
            tooltipTotalMonthlyDebtPayments: {
                default: 'debt-to-income-ratio-dti',
            },
            rentPayment: {
                default: '0',
            },
            labelRentPayment: {
                default: 'Rent Payment',
            },
            mortgagePayment: {
                default: '1900',
            },
            labelMortgagePayment: {
                default: 'Mortgage Payment',
            },
            homeEquityLoanPayment: {
                default: '0',
            },
            labelHomeEquityLoanPayment: {
                default: 'Home Equity Loan Payment',
            },
            creditCardPayment: {
                default: '350',
            },
            labelCreditCardPayment: {
                default: 'Credit Card Payment',
            },
            autoLoanOrLeasePayment: {
                default: '500',
            },
            labelAutoLoanOrLeasePayment: {
                default: 'Auto Loan or Lease Payment',
            },
            studentLoanPayment: {
                default: '0',
            },
            labelStudentLoanPayment: {
                default: 'Student Loan Payment',
            },
            personalLoanPayment: {
                default: '0',
            },
            labelPersonalLoanPayment: {
                default: 'Personal Loan Payment',
            },
            otherDebtPayments: {
                default: '0',
            },
            labelOtherDebtPayments: {
                default: 'Other Loan Payments',
            },
            ltLogo: {
                default: '0',
            },
            disclosureTokens: {
                default: '01,95',
            },
            disclosureText: {
                default: 'Disclosures',
            },
            disclosureId1: {
                default: 'Disclosures1',
            },
            disclosureId2: {
                default: 'Disclosures2',
            },
            showAdvancedOptions: {
                default: false,
            },
            debtToIncome: {
                default: 28,
            },
            span: {
                type: String,
                default: '',
            },
            debtToIncomeRatioDtiTooltip: {
                default: DefaultTooltip.DebtToIncomeRatioDti,
            },
            debtToIncomeRatioValue: {
                default: 20,
            },
            destinationLinkOrId: {
                default: 'wp-guru ',
            },
            destinationButtonText: {
                default: 'Get Free Offers',
            },
        },
        data() {
            return {
                AnnualIncome: this.annualIncome,
                TotalMonthlyDebtPayments: this.totalMonthlyDebtPayments,
                RentPayment: this.rentPayment,
                MortgagePayment: this.mortgagePayment,
                HomeEquityLoanPayment: this.homeEquityLoanPayment,
                CreditCardPayment: this.creditCardPayment,
                AutoLoanOrLeasePayment: this.autoLoanOrLeasePayment,
                StudentLoanPayment: this.studentLoanPayment,
                PersonalLoanPayment: this.personalLoanPayment,
                OtherDebtPayments: this.otherDebtPayments,
                DisclosureText: this.disclosureText,
                DisclosureId1: this.disclosureId1,
                DisclosureId2: this.disclosureId2,
                DisclosureTokens: this.disclosureTokens,
                ShowAdvancedOptions: this.showAdvancedOptions,
                HomePriceMsg: false,
                DebtToIncome: this.debtToIncome,
                DebtToIncomeRatioValue: this.debtToIncomeRatioValue,
                CalculatedValues: {},
                DebtToIncomeRatioDtiTooltip: Number.parseInt(this.hideTooltip, 10) ? '' : this.debtToIncomeRatioDtiTooltip,
                // Chart data
                pieChartData: {
                    labels: ['Monthly Debt', 'Remaining Monthly Income'],
                    datasets: [],
                },
                pieChartOptions: {
                    cutoutPercentage: 0,
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        titleFontStyle: 'bold',
                        callbacks: {
                            title(tooltipItems, data) {
                                const labelIndex = tooltipItems[0].index;
                                return data.labels[labelIndex];
                            },
                            label(tooltipItem, data) {
                                const value = `${Math.round(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index])}`;
                                return ` $${Accounting.formatNumber(value)}`;
                            },
                        },
                    },
                },
                // Setting for range slider
                slider: {
                    value: 20,
                    height: 20,
                    bgStyle: {
                        background: 'linear-gradient(to right, #08c177 10%, #08c177 10% 21%, #ffc507 21% 36%, #ff704d 36%)',
                    },
                    processStyle: {
                        backgroundColor: 'transparent',
                    },
                    tooltipAlways: 'always',
                    clickable: false,
                    disabled: true,
                },
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
            };
        },
        computed: {
            NetIncome() {
                return this.formatFigure(this.unformat(this.AnnualIncome) / 12 - this.unformat(this.TotalMonthlyDebtPayments));
            },
            GrossMonthlyIncome() {
                return this.formatFigure(this.unformat(this.AnnualIncome) / 12);
            },
            debtToIncomeRatio() {
                let totalMonthlyDebt = this.unformat(this.PersonalLoanPayment) + this.unformat(this.StudentLoanPayment) + this.unformat(this.AutoLoanOrLeasePayment) + this.unformat(this.CreditCardPayment) + this.unformat(this.HomeEquityLoanPayment) + this.unformat(this.MortgagePayment) + this.unformat(this.RentPayment) + this.unformat(this.OtherDebtPayments);
                if (totalMonthlyDebt === 0) {
                    totalMonthlyDebt = this.unformat(this.TotalMonthlyDebtPayments) > 0 ? this.unformat(this.TotalMonthlyDebtPayments) : 1;
                }
                const grossMonthlyIncome = this.unformat(this.AnnualIncome) / 12 > 0 ? this.unformat(this.AnnualIncome) / 12 : 0;
                const ratio = totalMonthlyDebt / grossMonthlyIncome;
                // SIDE EFFECT
                this.DebtToIncomeRatioValue = ratio.toFixed(2) * 100 > 100 ? 100 : ratio.toFixed(2) * 100;
                return this.DebtToIncomeRatioValue.toFixed(0);
            },
        },
        watch: {
            TotalMonthlyDebtPayments: {
                handler() {
                    this.TotalMonthlyDebtPayments = this.unformatFigure(this.TotalMonthlyDebtPayments) > 0 ? this.unformatFigure(this.TotalMonthlyDebtPayments) : 1;
                    this.setPieChartData();
                },
            },
            AnnualIncome: {
                handler() {
                    this.AnnualIncome = this.unformatFigure(this.AnnualIncome) > 0 ? this.unformatFigure(this.AnnualIncome) : 1;
                    this.setPieChartData();
                },
            },
        },
        mounted() {
            const monthlyIncome = this.AnnualIncome / 12;
            const NetIncome = this.unformat(monthlyIncome) - this.unformat(this.TotalMonthlyDebtPayments);

            this.pieChartData.datasets = this.getDataSet(this.TotalMonthlyDebtPayments, NetIncome);
        },
        methods: {
            getDataSet(a, b) {
                return [{
                    data: [a, b],
                    backgroundColor: [
                        '#08c177',
                        '#078181',
                    ],
                    borderWidth: 1,
                }];
            },
            unformat(val) {
                return Accounting.unformat(val);
            },
            viewMoreClick() {
                this.ShowAdvancedOptions = !this.ShowAdvancedOptions;
                this.TotalMonthlyDebtPayments = this.formatFigure(this.TotalMonthlyDebtPayments);
            },
            resetFilter() {
                this.AnnualIncome = this.formatFigure(this.annualIncome);
                this.TotalMonthlyDebtPayments = this.formatFigure(this.totalMonthlyDebtPayments);
                this.RentPayment = this.formatFigure(this.rentPayment);
                this.MortgagePayment = this.formatFigure(this.mortgagePayment);
                this.HomeEquityLoanPayment = this.formatFigure(this.homeEquityLoanPayment);
                this.CreditCardPayment = this.formatFigure(this.creditCardPayment);
                this.AutoLoanOrLeasePayment = this.formatFigure(this.autoLoanOrLeasePayment);
                this.StudentLoanPayment = this.formatFigure(this.studentLoanPayment);
                this.PersonalLoanPayment = this.formatFigure(this.personalLoanPayment);
                this.OtherDebtPayments = this.formatFigure(this.otherDebtPayments);
            },
            updateTotalMonthlyDebtPayments() {
                this.TotalMonthlyDebtPayments = this.formatFigure(this.unformat(this.RentPayment) + this.unformat(this.MortgagePayment) + this.unformat(this.HomeEquityLoanPayment) + this.unformat(this.CreditCardPayment) + this.unformat(this.AutoLoanOrLeasePayment) + this.unformat(this.StudentLoanPayment) + this.unformat(this.PersonalLoanPayment) + this.unformat(this.OtherDebtPayments));
            },
            updatePaymentValues() {
                this.RentPayment = 0;
                this.MortgagePayment = 0;
                this.HomeEquityLoanPayment = 0;
                this.CreditCardPayment = 0;
                this.AutoLoanOrLeasePayment = 0;
                this.StudentLoanPayment = 0;
                this.PersonalLoanPayment = 0;
                this.OtherDebtPayments = this.TotalMonthlyDebtPayments;
            },
            currencyFilter(figure) {
                return Accounting.formatNumber(figure);
            },
            setColor(value) {
                if (value < 21) return 'debt-income-green';
                if (value >= 21 && value < 36) return 'debt-income-yellow';
                if (value >= 36) return 'debt-income-red';
                return '';
            },
            setPieChartData() {
                this.pieChartData.datasets = this.getDataSet(this.unformat(this.TotalMonthlyDebtPayments), this.unformat(this.NetIncome));
            },
        },
    };
</script>
<style src="../../assets/styles/v2/common.scss" lang="scss"></style>
<style src="../../assets/styles/v2/calc-advanced-filter.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
